import { Dispatch, FC, memo, SetStateAction, useState } from 'react'
import FundraisingSelect from '../FundraisingSelect'
import { Flex, SystemProp } from '@mantine/core'
import { Button, ButtonRootVariable, Input } from '@gate/gui'
import { replaceValue, stopEvent } from '@/utils/tools'
import { IColumnsProps } from '..'
import { useTranslation } from 'next-i18next'
import { CSSProperties } from 'styled-components'
import { observer } from 'mobx-react-lite'
import useRateChange from '@/hooks/useRateChange'

interface FundraisingSelectProps {
  rounds: string
  tag: string
  investor: string
  amount_start: string
  amount_end: string
  min: string
  max: string
  isInput: boolean
}

export interface FundraisingAmountProps {
  title: string
  justify?: SystemProp<CSSProperties['justifyContent']>
  fundraisingSelect: FundraisingSelectProps
  setFundraisingSelect: Dispatch<SetStateAction<FundraisingSelectProps>>
  onFundraisingSelectChange: IColumnsProps['onFundraisingSelectChange']
}

const FundraisingAmount: FC<FundraisingAmountProps> = ({
  title,
  fundraisingSelect,
  setFundraisingSelect,
  onFundraisingSelectChange,
}) => {
  const [flag, setFlag] = useState(false)
  const [disabled, setDisabled] = useState(true)
  const [error, setError] = useState(false)

  const { t } = useTranslation()
  const { faitRateItem } = useRateChange()

  const handleMinChange = (e: any) => {
    const value = replaceValue(e.target.value)
    setFundraisingSelect(params => ({
      ...params,
      min: value,
    }))

    if (!value?.length) {
      setError(false)
      setDisabled(true)
      return
    }

    // 检查 min 是否小于 max
    const min = Number(value)
    const max = Number(fundraisingSelect.max)
    if (min < max) {
      setDisabled(false)
      setError(false)
    } else {
      setDisabled(true)
      setError(true)
    }
  }

  const handleMaxChange = (e: any) => {
    const value = replaceValue(e.target.value)

    setFundraisingSelect(params => ({
      ...params,
      max: value,
    }))

    if (!fundraisingSelect?.min?.length) {
      setError(false)
      setDisabled(true)
      return
    }

    // 检查 min 是否小于 max
    const min = Number(fundraisingSelect.min)
    const max = Number(value)
    if (min < max) {
      setDisabled(false)
      setError(false)
    } else {
      setDisabled(true)
      setError(true)
    }
  }

  return (
    <Flex w="100%" justify="flex-start">
      <FundraisingSelect
        key={'key' + faitRateItem.symbol}
        w={257}
        miw={257}
        h={'auto'}
        title={title}
        data={{
          list: [
            {
              value: '0&1000000',
              label: `<${faitRateItem?.symbol}1M`,
            },
            {
              value: '1000000&5000000',
              label: `${faitRateItem?.symbol}1M-${faitRateItem?.symbol}5M`,
            },
            {
              value: '5000000&10000000',
              label: `${faitRateItem?.symbol}5M-${faitRateItem?.symbol}10M`,
            },
            {
              value: '10000000&30000000',
              label: `${faitRateItem?.symbol}10M-${faitRateItem?.symbol}30M`,
            },
            {
              value: '30000000&50000000',
              label: `${faitRateItem?.symbol}30M-${faitRateItem?.symbol}50M`,
            },
            {
              value: '50000000&100000000',
              label: `${faitRateItem?.symbol}50M-${faitRateItem?.symbol}100M`,
            },
            {
              value: '100000000&',
              label: `>=${faitRateItem?.symbol}100M`,
            },
          ],
        }}
        isSearch={false}
        isMultiple={false}
        extra={
          <div>
            {flag ? (
              <div>
                <Flex align="center" gap={4} mt={8} pl={8}>
                  <Input
                    w={'100%'}
                    invalid={error}
                    placeholder={'Min(M)'}
                    value={fundraisingSelect.min}
                    onChange={handleMinChange}
                    onClear={() => handleMinChange({ target: { value: '' } })}
                  />

                  <span style={{ color: 'var(--color-icon-4)' }}>-</span>

                  <Input
                    invalid={error}
                    w={'100%'}
                    placeholder={'Max(M)'}
                    value={fundraisingSelect.max}
                    onChange={handleMaxChange}
                    onClear={() => handleMaxChange({ target: { value: '' } })}
                  />
                </Flex>

                <Flex justify="flex-end" mt={16} pb={10} pl={8}>
                  <Button
                    id="close"
                    disabled={disabled}
                    size="md"
                    label={t('p.confirm', { ns: 'fundraising' }) + ''}
                    onClick={() => {
                      setFundraisingSelect(params => ({
                        ...params,
                        amount_start: '',
                        amount_end: '',
                      }))
                      onFundraisingSelectChange(
                        'amount',
                        Number(fundraisingSelect.min) * 1000000 + '&' + Number(fundraisingSelect.max) * 1000000
                      )
                    }}
                  />
                </Flex>
              </div>
            ) : (
              <Flex justify="flex-start" mt={16} pb={10} pl={8}>
                <Button
                  styles={{
                    root: {
                      [ButtonRootVariable.BUTTON_COLOR]: 'var(--color-text-5)',
                      [ButtonRootVariable.BUTTON_HOVER_COLOR]: 'var(--color-brand-2)',
                    },
                  }}
                  type="text"
                  size="md"
                  leftIcon={
                    <svg className="iconpark-icon">
                      <use href="#icon-20px-line-bianji-20px"></use>
                    </svg>
                  }
                  label={t('p.custom', { ns: 'fundraising' }) + ''}
                  onClick={e => {
                    stopEvent(e)
                    setFlag(true)
                  }}
                />
              </Flex>
            )}
          </div>
        }
        value={
          fundraisingSelect.amount_start === ' '
            ? ' '
            : fundraisingSelect.amount_start + '&' + fundraisingSelect.amount_end
        }
        onChange={value => {
          const map = value?.split('&')
          setFundraisingSelect(params => ({
            ...params,
            min: '',
            max: '',
            amount_start: map?.[0] ?? ' ',
            amount_end: map?.[1] ?? ' ',
          }))
          onFundraisingSelectChange('amount', value)
        }}
      />
    </Flex>
  )
}

export default memo(observer(FundraisingAmount))
